import * as React from 'react';
import {Dayjs} from 'dayjs';
import {Alert, Box, Button, ButtonGroup, CircularProgress, Typography} from '@mui/material';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import StopIcon from '@mui/icons-material/Stop';
import ReplayIcon from '@mui/icons-material/Replay';
import {useBriefing} from '../../data/briefing';
import {BriefingPeriod} from '../../contract';
import {apiErrorMessage} from '../../utilities/apiError';
import {audioBlob, briefingRequest} from '../../utilities/briefing';

// A silent clip. Browsers (Safari above all) only let a page start sound from a click, and a summary takes several seconds
// to arrive, so the player is started on the click with this and given the real recording when it comes.
const SILENCE = 'data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=';

type Status = 'idle' | 'preparing' | 'playing' | 'ready';

const PERIODS: BriefingPeriod[] = ['day', 'week', 'nextWeek'];

// Three buttons that each play a spoken summary of the board's tasks: for the day the display is showing, for this week, and
// for next week. The server has a language model write the summary and a voice read it out. One player is shared, so starting
// one stops whatever else was playing; the text is shown while it plays, and any of them can be played again without asking again.
export function BriefingButtons({boardId, today, selectedDate}: { boardId: string, today: Dayjs, selectedDate: Dayjs }) {
    const {mutate: requestBriefing, error, reset} = useBriefing(boardId);
    const [active, setActive] = React.useState<BriefingPeriod | null>(null);
    const [status, setStatus] = React.useState<Status>('idle');
    const [summary, setSummary] = React.useState<string | null>(null);
    const [playbackFailed, setPlaybackFailed] = React.useState(false);
    const audioRef = React.useRef<HTMLAudioElement | null>(null);
    const urlRef = React.useRef<string | null>(null);
    const timeZone = React.useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);

    const labels: Record<BriefingPeriod, string> = {
        day: selectedDate.isSame(today, 'day') ? 'Today' : selectedDate.format('dddd'),
        week: 'This week',
        nextWeek: 'Next week',
    };
    // The same, as it reads in a sentence ("a summary of today", "of Friday", "of next week").
    const subjects: Record<BriefingPeriod, string> = { ...labels, day: selectedDate.isSame(today, 'day') ? 'today' : labels.day, week: 'this week', nextWeek: 'next week' };

    const release = () => {
        audioRef.current?.pause();
        audioRef.current = null;
        if (urlRef.current) URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
    };

    const clear = () => {
        release();
        reset();
        setActive(null);
        setStatus('idle');
        setSummary(null);
        setPlaybackFailed(false);
    };

    // Leaving the page must not leave it talking.
    React.useEffect(() => release, []);

    // A summary is about a particular day or week; once the display moves on (another day chosen, midnight passes), it no
    // longer matches what is on screen.
    const coveredStart = active ? briefingRequest(active, {today, selected: selectedDate}, timeZone).start : null;
    const startedFor = React.useRef<string | null>(null);
    React.useEffect(() => {
        if (active && startedFor.current && coveredStart !== startedFor.current) clear();
    }, [coveredStart]);

    const play = (audio: HTMLAudioElement) => {
        setPlaybackFailed(false);
        audio.currentTime = 0;
        audio.play().then(() => setStatus('playing')).catch(() => {
            // The browser refused to start sound on its own; pressing the button again is a click it will accept.
            setPlaybackFailed(true);
            setStatus('ready');
        });
    };

    const start = (period: BriefingPeriod) => {
        release();
        reset();
        setSummary(null);
        setPlaybackFailed(false);
        setActive(period);
        setStatus('preparing');

        const request = briefingRequest(period, {today, selected: selectedDate}, timeZone);
        startedFor.current = request.start;

        const audio = new Audio(SILENCE);
        audioRef.current = audio;
        audio.play().catch(() => undefined);

        requestBriefing(request, {
            onSuccess: (briefing) => {
                if (audioRef.current !== audio) return;     // stopped or cleared while it was being written

                urlRef.current = URL.createObjectURL(audioBlob(briefing.audio, briefing.contentType));
                audio.src = urlRef.current;
                audio.onended = () => setStatus('ready');
                audio.onerror = () => { setPlaybackFailed(true); setStatus('ready'); };
                setSummary(briefing.summary);
                play(audio);
            },
            onError: () => {
                release();
                setActive(null);
                setStatus('idle');
            },
        });
    };

    const onClick = (period: BriefingPeriod) => {
        if (period === active && status === 'playing') {
            audioRef.current?.pause();
            return setStatus('ready');
        }
        if (period === active && status === 'ready' && audioRef.current) return play(audioRef.current);
        start(period);
    };

    const iconFor = (period: BriefingPeriod) => {
        if (period !== active) return <RecordVoiceOverIcon/>;
        return status === 'preparing' ? <CircularProgress size={18}/>
            : status === 'playing' ? <StopIcon/>
                : status === 'ready' ? <ReplayIcon/>
                    : <RecordVoiceOverIcon/>;
    };

    const actionFor = (period: BriefingPeriod) => {
        if (period !== active) return 'Play a summary of';
        return status === 'preparing' ? 'Writing the summary of'
            : status === 'playing' ? 'Stop the summary of'
                : status === 'ready' ? 'Play again the summary of'
                    : 'Play a summary of';
    };

    return (
        <Box>
            <Typography variant="body2" sx={{fontWeight: 600, mb: 1}}>Listen to a summary</Typography>
            <Box role="group" aria-label="Spoken summaries" sx={{display: 'flex', flexWrap: 'wrap', gap: 1}}>
                {PERIODS.map((period) => (
                    <Button
                        key={period}
                        variant={period === active && status !== 'idle' ? 'contained' : 'outlined'}
                        disabled={status === 'preparing'}
                        onClick={() => onClick(period)}
                        startIcon={iconFor(period)}
                        aria-label={`${actionFor(period)} ${subjects[period]}`}
                    >
                        {period === active && status === 'preparing' ? 'Preparing…' : period === active && status === 'playing' ? `Stop · ${labels[period]}` : labels[period]}
                    </Button>
                ))}
            </Box>

            <Box aria-live="polite">
                {status === 'preparing' && (
                    <Typography variant="body2" color="text.secondary" sx={{mt: 1.5}}>Writing the summary…</Typography>
                )}
                {summary && active && (
                    <Typography variant="body1" color="text.secondary" sx={{mt: 1.5, maxWidth: 640}}>
                        <Box component="span" sx={{fontWeight: 600, color: 'text.primary'}}>{labels[active]}: </Box>
                        {summary}
                    </Typography>
                )}
                {error && <Alert severity="warning" sx={{mt: 1.5, maxWidth: 640}}>{apiErrorMessage(error)}</Alert>}
                {playbackFailed && (
                    <Alert severity="info" sx={{mt: 1.5, maxWidth: 640}}>Your browser blocked the sound. Press the button to play it.</Alert>
                )}
            </Box>
        </Box>
    );
}
